import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  MibBreakpointService,
  MibButtonComponent,
  MibIconsModule,
  MibTooltipDirective,
  updateTargetHeight,
} from '@medinbox/angular';

@Component({
  selector: 'mai-prompt-box',
  imports: [MibButtonComponent, MibIconsModule, ReactiveFormsModule, MibTooltipDirective],
  templateUrl: './prompt-box.html',
  styleUrl: './prompt-box.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PromptBox {
  private readonly breakpointService = inject(MibBreakpointService);

  readonly hidePTT = input(false, { transform: booleanAttribute });
  readonly displayPlusButton = input(false, { transform: booleanAttribute });
  readonly placeholder = input<string>('Ask any question about the case');
  readonly chatLocked = input(false, { transform: booleanAttribute });
  readonly isPttActive = input(false, { transform: booleanAttribute });
  readonly pttTranscription = input('');

  readonly messageSent = output<string>();
  readonly pttStart = output<void>();
  readonly pttStop = output<void>();

  private readonly defaultTextareaHeight = 24;
  private readonly defaultPromptHeight = computed(() =>
    this.breakpointService.isMobileLandscape() ? 48 : 64,
  );
  protected readonly userPrompt = new FormControl('', { nonNullable: true });

  readonly textareaElement = viewChild.required<ElementRef<HTMLTextAreaElement>>('textarea');
  readonly biggerPrompt = signal(false);
  readonly promptBoxHeight = signal(this.defaultPromptHeight());
  readonly noLeftButton = computed(() => !this.displayPlusButton() && this.hidePTT());

  readonly textareaPosition = computed(() => {
    const alignLeftClasses = 'left-5 pr-6 -mr-6 max-lg:landscape:top-1 max-lg:landscape:left-3';
    if (this.biggerPrompt()) {
      return `${alignLeftClasses} right-2 top-2  max-lg:landscape:right-1`;
    } else if (this.noLeftButton()) {
      return `${alignLeftClasses} right-16`;
    } else {
      return 'left-16 right-16';
    }
  });

  sendPrompt(): void {
    if (!this.chatLocked()) {
      const text = this.userPrompt.getRawValue().trim();
      if (!text) return;
      this.messageSent.emit(text);
      this.userPrompt.setValue('');
      this.resetPromptHeight();
      // On mobile, release focus so the on-screen keyboard dismisses itself.
      // Kept desktop-focused so power users can keep typing.
      if (this.breakpointService.isBaseOrSm() || this.breakpointService.isMobileLandscape()) {
        this.textareaElement().nativeElement.blur();
      }
    }
  }

  /**
   * adjust textarea size to its content without going over a certain limit (about 4 lines)
   * @param event
   */
  updatePromptHeight(event: Event): void {
    const newHeight = this.breakpointService.isMobileLandscape()
      ? updateTargetHeight(event, this.defaultPromptHeight())
      : updateTargetHeight(event);
    if (newHeight > this.defaultTextareaHeight) {
      this.biggerPrompt.set(true);
      this.promptBoxHeight.set(newHeight + this.defaultPromptHeight());
    } else if (!this.userPrompt.value) {
      this.resetPromptHeight();
    }
  }

  /**
   * Reset prompt height to its default size
   */
  resetPromptHeight(): void {
    this.biggerPrompt.set(false);
    this.promptBoxHeight.set(this.defaultPromptHeight());
    this.textareaElement().nativeElement.style.height = `${this.defaultTextareaHeight}px`;
  }

  /**
   * Set the prompt value (e.g. from a PTT final transcription).
   */
  setPrompt(prompt: string): void {
    this.userPrompt.setValue(prompt);
  }
}
